import * as React from 'react'
import { useMemo, useState } from 'react'

import { Autocomplete, Box, TableContainer, TextField } from '@mui/material'
import PageSection from '../../../Constants&Theme/PageSection'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { appBar } from '../../../garvan-react/Constants/StyleConstants'
import { useResultsReportCols } from './ResultsReportCols'
import { useGetResultsReportData } from '../../../APIs/Hooks/ScreenHooks'
import ResultsReportDGToolbar, {
  ResultsFilters,
} from './ResultsReportDGToolbar'
import { stackGap } from '../../../Constants&Theme/Spacing'

function ResultsReport() {
  const [screenIDs, setScreenIDs] = useState<any[]>([])
  const [dgFilters, setDGFilters] = useState<Partial<ResultsFilters>>({})
  const cols = useResultsReportCols()

  const resultQueries = useGetResultsReportData(
    [...new Set(screenIDs)],
    setScreenIDs,
  )

  const combinedResults: any[] = useMemo(() => {
    let results: any[] = []

    resultQueries
      .filter((q) => q.data?.samples) //have data
      .forEach((q) => {
        results = results.concat(Object.values(q.data.samples))
      })

    const filteredResults = results
      .filter(
        (s) =>
          !dgFilters?.FacilityID ||
          s?.DestinationFacilityID === dgFilters?.FacilityID,
      )
      .filter(
        (s) =>
          !dgFilters?.SampleType || s?.SampleType === dgFilters?.SampleType,
      )
      .filter((s) => !dgFilters?.ResultID || s?.Result === dgFilters?.ResultID)

    const aggregated: any = {}
    filteredResults.forEach((row) => {
      const newRow = aggregated?.[row.SampleID] ?? {}
      Object.entries(row).forEach(([key, value]) => {
        if (['AnimalID', 'RackID', 'ShortLineName'].includes(key)) {
          if (value) {
            if (newRow?.[key]) {
              newRow[key] = [
                ...new Set([...newRow?.[key].split(', '), String(value)]),
              ].join(', ')
            } else {
              newRow[key] = String(value)
            }
            // clear AnimalID if RackID has value - should be exclusive result, but Animal ID always present
            if (newRow?.['RackID']) {
              newRow['AnimalID'] = null
            }
          }
        } else {
          newRow[key] = value
        }
      })
      aggregated[row.SampleID] = newRow
    })
    return Object.values(aggregated)
  }, [resultQueries, dgFilters])
  return (
    <Box
      sx={{
        maxWidth: 'xl',
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <PageSection title={'Screen Results Report'}>
        <Autocomplete
          fullWidth
          multiple
          sx={{ mb: stackGap.col }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={'Screen IDs'}
            />
          )}
          onChange={async (event, newValue) => {
            setScreenIDs(newValue)
            setDGFilters({})
          }}
          options={[]}
          freeSolo
          value={screenIDs}
        />
        <TableContainer
          sx={{ height: `calc(100vh - ${appBar}px - 300px)`, width: 1 }}
        >
          <DataGridPro
            getRowId={(row) => row.SampleID}
            rows={combinedResults}
            columns={cols}
            disableRowSelectionOnClick
            loading={resultQueries.some((q) => q.isLoading)}
            disableColumnMenu
            slots={{
              toolbar: ResultsReportDGToolbar as any,
            }}
            slotProps={{
              toolbar: {
                dgFilters,
                setDGFilters,
              },
            }}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  ScreenID: false,
                  RackID: false,
                  AnimalID: false,
                  ShortLineName: false,
                },
              },
            }}
          />
        </TableContainer>
      </PageSection>
    </Box>
  )
}

export default ResultsReport
